import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, CircularProgress, Grid, Typography } from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import PropTypes from "prop-types";

import CalenderView from "Components/Common/CalenderView";
import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import Backdrop from "Components/Common/Backdrop";
import SelectField from "Components/Common/SelectField";
import { FIRE_SAFETY_SUPER_ADMIN } from "common/constant";

import { isAuthorizedUser } from "utils";

import {
  useGetDeveloperBuildingsQuery,
  usePostRescheduleMutation,
} from "apis/AuditSchedular";

const otherBuildingId = 0;

const SchedularApprovalModal = ({
  applicationId,
  t,
  handleClose,
  buildingName,
}) => {
  const { data: { data: buildingsData } = {}, isLoading } =
    useGetDeveloperBuildingsQuery();

  const [addSchedularDates, { isLoading: isSchedularDatesAdding }] =
    usePostRescheduleMutation();

  const { roles } = useSelector((state) => state.Login);

  const [selectedScheduleDatesData, setSelectedScheduleDatesData] = useState(
    []
  );

  const formik = useFormik({
    initialValues: {
      buildingId: "",
      otherBuildingName: "",
    },
    validationSchema: Yup.object({
      ...(isAuthorizedUser(roles, [FIRE_SAFETY_SUPER_ADMIN]) && {
        buildingId: Yup.string().required("Building is required"),
        otherBuildingName: Yup.string().test(
          "isOtherBuildingNameRequired",
          "",
          function () {
            const [{ value: current = null } = {}] = this.from || [];

            if (
              current.buildingId === otherBuildingId &&
              !current.otherBuildingName
            ) {
              return this.createError({
                message: "Building name is required",
              });
            }
            return true;
          }
        ),
      }),
    }),
    onSubmit: async () => {
      try {
        await addSchedularDates({
          buildingInformationId: applicationId,
          buildingSchedules: selectedScheduleDatesData,
        });
        handleClose();
      } catch (error) {}
    },
  });

  const {
    values: { buildingId },
    handleSubmit,
    setFieldValue,
  } = formik;

  const updatedBuildingOptions = useMemo(
    () => [
      ...(buildingsData?.map(({ name }) => ({ label: name, value: name })) ??
        []),
      { value: 0, label: "Other" },
    ],
    [buildingsData]
  );

  if (isLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={() => handleClose(false)}
      maxWidth="lg"
      title={`${t("Audit Schedule")} - ${buildingName}`}
    >
      <CalenderView
        onDateChange={setSelectedScheduleDatesData}
        buildingName={buildingName}
      />

      <Grid container className="mt-3" columnSpacing={2}>
        {isAuthorizedUser(roles, [FIRE_SAFETY_SUPER_ADMIN]) && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" className="mb-2">
                Building Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectField
                label={t("Building") + "*"}
                name="buildingId"
                onChange={(_, { value }) => {
                  setFieldValue("buildingId", value);
                  setFieldValue("otherBuildingName", "");
                }}
                formik={formik}
                options={updatedBuildingOptions || []}
              />
            </Grid>

            {buildingId === otherBuildingId && (
              <Grid item xs={12} sm={6}>
                <InputFieldText
                  label={t("Building Name") + "*"}
                  name="otherBuildingName"
                  formik={formik}
                  placeholder="Enter Building Name"
                />
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12} className="d-flex justify-content-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            endIcon={isSchedularDatesAdding && <CircularProgress size={20} />}
            disabled={isSchedularDatesAdding}
          >
            {t("Submit")}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

SchedularApprovalModal.propTypes = {
  t: PropTypes.func,
  handleClose: PropTypes.func,
  applicationId: PropTypes.number,
  buildingName: PropTypes.string,
};

export default withTranslation()(SchedularApprovalModal);
